/* public-i18n-migrated */
import { useI18n } from "@/i18n";
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AllRounderLogo from "@/components/AllRounderLogo";

const DEFAULT_PROD_URL = "https://allrounderound.com";
const BBQ_URL = "https://allrounderound.com/bbq";

export default function QRPage() {
  const { tx } = useI18n();

  const [params] = useSearchParams();
  const canvasWrapRef = useRef(null);
  const initialUrl = params.get("url") || DEFAULT_PROD_URL;
  const [url, setUrl] = useState(initialUrl);
  const [size, setSize] = useState(560);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "All Rounder · QR Code";
  }, []);

  const safeUrl = useMemo(() => url.trim() || DEFAULT_PROD_URL, [url]);

  const handleDownload = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error(tx("QR을 찾지 못했어요"));
      return;
    }
    const link = document.createElement("a");
    link.download = "all-rounder-qr.png";
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(tx("PNG 다운로드 완료"));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(safeUrl);
      setCopied(true);
      toast.success(tx("링크 복사됨"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tx("복사 실패"));
    }
  };

  return (
    <main
      data-testid="qr-page"
      className="min-h-screen bg-[#ececec] py-0 sm:py-8 print:bg-white print:py-0"
    >
      <div className="w-full max-w-md mx-auto bg-[#FAFAFA] min-h-screen sm:min-h-0 sm:rounded-md shadow-2xl shadow-black/5 overflow-hidden print:shadow-none print:max-w-none print:bg-white">
        {/* Header — hidden on print */}
        <div className="px-5 h-14 flex items-center justify-between border-b border-black/5 bg-white/85 backdrop-blur-xl print:hidden">
          <Link
            to="/"
            data-testid="qr-back-link"
            className="flex items-center gap-1.5 text-sm font-semibold text-[#52525B] hover:text-[#121212] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{tx("Back")}</span>
          </Link>
          <PublicLanguageSelect id="qr-language"/>
        </div>

        {/* Printable card */}
        <section
          data-testid="qr-printable"
          className="px-6 pt-10 pb-8 print:px-12 print:pt-16"
        >
          <p className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#0A369D] flex items-center gap-2">
            <span className="inline-block w-6 h-px bg-[#0A369D]" /> {tx("SCAN ME")} </p>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tighter leading-[0.95] text-[#121212]"> {tx("카메라로 스캔하세요.")} </h1>
          <p className="mt-2 text-sm text-[#52525B]"> {tx("Scan with your phone camera to open All Rounder.")} </p>

          <div
            ref={canvasWrapRef}
            data-testid="qr-canvas-wrap"
            className="mt-8 mx-auto w-fit p-5 bg-white border border-black/10 rounded-md print:border-2 print:border-black"
          >
            <QRCodeCanvas
              value={safeUrl}
              size={size}
              level="H"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0F2A4F"
              imageSettings={{
                src:
                  "data:image/svg+xml;utf8," +
                  encodeURIComponent(`
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>
                      <rect width='100' height='100' rx='12' fill='#0F2A4F'/>
                      <text x='50' y='66' text-anchor='middle' font-family='Cinzel, Playfair Display, serif' font-weight='700' font-size='40' fill='#FFFFFF' letter-spacing='-1'>AR</text>
                    </svg>
                  `),
                height: Math.round(size * 0.16),
                width: Math.round(size * 0.16),
                excavate: true,
              }}
              style={{ width: "min(72vw, 320px)", height: "min(72vw, 320px)" }}
            />
          </div>

          <div className="mt-6 flex flex-col items-center text-center">
            <AllRounderLogo variant="lockup" className="items-center" />
            <div className="mt-3 text-xs uppercase tracking-[0.22em] font-semibold text-[#52525B]"> {tx("Brisbane · Gold Coast")} </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#52525B]"> {tx("Agency · Hotel · Study · Working Holiday · Tasker")} </div>
            <div className="mt-3 text-[11px] font-medium text-[#0A369D] break-all">
              {safeUrl}
            </div>
          </div>
        </section>

        {/* Controls — hidden on print */}
        <section className="px-6 pb-12 print:hidden">
          <div className="space-y-1.5 mt-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#52525B]">{tx("Presets")}</Label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setUrl(DEFAULT_PROD_URL)}
                data-testid="qr-preset-home"
                className={`px-3 py-2 rounded-md text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                  url === DEFAULT_PROD_URL
                    ? "border-[#0A369D] bg-[#0A369D] text-white"
                    : "border-black/15 text-[#52525B] hover:border-[#0A369D] hover:text-[#0A369D]"
                }`}
              > {tx("Home")} </button>
              <button
                type="button"
                onClick={() => setUrl(BBQ_URL)}
                data-testid="qr-preset-bbq"
                className={`px-3 py-2 rounded-md text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                  url === BBQ_URL
                    ? "border-[#E85A2C] bg-[#E85A2C] text-white"
                    : "border-black/15 text-[#52525B] hover:border-[#E85A2C] hover:text-[#E85A2C]"
                }`}
              > {tx("BBQ Party")} </button>
            </div>
          </div>

          <div className="space-y-1.5 mt-4">
            <Label
              htmlFor="qr-url"
              className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#52525B]"
            > {tx("QR이 가리킬 URL")} </Label>
            <Input
              id="qr-url"
              data-testid="qr-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={DEFAULT_PROD_URL}
              className="h-11 rounded-md border-black/15 focus-visible:ring-2 focus-visible:ring-[#0A369D] focus-visible:border-transparent"
            />
          </div>

          <div className="space-y-1.5 mt-4">
            <Label
              htmlFor="qr-size"
              className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#52525B]"
            > {tx("해상도 —")} {size}{tx("px (다운로드 PNG)")} </Label>
            <input
              id="qr-size"
              data-testid="qr-size-slider"
              type="range"
              min={240}
              max={1200}
              step={40}
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value, 10))}
              className="w-full accent-[#0A369D]"
            />
            <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] font-semibold text-[#52525B]">
              <span>{tx("웹용 240px")}</span>
              <span>{tx("인쇄용 1200px")}</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-2.5">
            <Button
              data-testid="qr-download-btn"
              onClick={handleDownload}
              className="h-12 rounded-md bg-[#0A369D] hover:bg-[#08297A] text-white font-semibold text-[15px]"
            >
              <Download className="w-4 h-4 mr-2" /> {tx("PNG 다운로드")} </Button>
            <Button
              data-testid="qr-print-btn"
              onClick={handlePrint}
              variant="outline"
              className="h-12 rounded-md bg-white border border-black/10 hover:bg-[#EBF1FF] hover:text-[#0A369D] hover:border-[#0A369D]/30 text-[#121212] font-semibold text-[15px]"
            >
              <Printer className="w-4 h-4 mr-2" /> {tx("바로 인쇄")} </Button>
            <Button
              data-testid="qr-copy-btn"
              onClick={handleCopy}
              variant="ghost"
              className="h-11 rounded-md font-semibold text-[14px] text-[#52525B] hover:text-[#121212] hover:bg-black/5"
            >
              {copied ? (
                <Check className="w-4 h-4 mr-2 text-[#0A369D]" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied ? tx("복사됨") : tx("링크 복사")}
            </Button>
          </div>

          <p className="mt-6 text-[11px] text-[#52525B] leading-relaxed"> {tx("팁: 인쇄용은")} <b>{tx("1000px 이상")}</b>{tx("으로 다운로드하면 명함/포스터에서도 깨끗하게 스캔됩니다. 오류 보정 레벨 H가 적용되어 로고가 들어가도 인식이 잘 됩니다.")} </p>
        </section>
      </div>
    </main>
  );
}
